export type Predicate<T> = (value: T) => boolean;
export type PartialSome<T, Keys extends keyof T> = Partial<Pick<T, Keys>> & Omit<T, Keys>;
export type CountElementsResult<C extends Record<string, unknown>> = { [K in keyof C]: number };

/**
 * Count occurences of item in iterable sequence.
 * Automatically curries, so you can give just first parameter and get back a function that
 * accepts sequence and returns count.
 * @param itemOrPredicate - item or function to test for item
 * @param sequence - the iterable
 * @returns number
 */
export function count<T>(itemOrPredicate: T | Predicate<T>, sequence: Iterable<T>) {
  let result = 0;
  if (itemOrPredicate instanceof Function) {
    for (const item of sequence) if (itemOrPredicate(item)) result++;
  } else {
    for (const item of sequence) if (item == itemOrPredicate) result++;
  }
  return result;
}

export function countFunction<T>(itemOrPredicate: T | Predicate<T>) {
  return (sequence: Iterable<T>) => count(itemOrPredicate, sequence);
}

/**
 * Counts multiple criteria in an array of elements.
 * @param criteria - an object where the keys are the names of the criteria and the values are predicates
 * @param elements - an array of elements to count the criteria in
 * @returns an object where the keys are the names of the criteria and the values are the counts
 */
export function countByPredicates<T, C extends Record<string, Predicate<T>>>(
  criteria: C,
  elements: T[]
): CountElementsResult<C> {
  const list = Object.entries(criteria);

  const results: CountElementsResult<C> = list.reduce((acc, [key]) => {
    acc[key as keyof C] = 0;
    return acc;
  }, {} as CountElementsResult<C>);
  for (const element of elements) {
    for (const [testName, predicate] of list) {
      if (predicate(element)) {
        results[testName as keyof C]++;
      }
    }
  }
  return results;
}
